import { useTranslation } from "react-i18next";
import { Panel } from 'primereact/panel';
import { useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import Chart from "highcharts-react-official";
import graficoDashboardRepresentante from '../../service/dashboardRepresentante';
import { formatarParaMoedaBRL } from '../../utils';
import PodiumPanel from './PodiumPanel';

type ValorItem = { label: string; valor: number };
type QuantidadeItem = { label: string; quantidade: number };
type SerieTemporal = { categorias: string[]; series: { nome: string; dados: number[] }[] };

// Paleta categórica para as séries por representante (visão do gestor).
const CORES_SERIE = ['#7c3aed', '#0ea5e9', '#22c55e', '#f97316', '#ef4444', '#eab308', '#14b8a6', '#ec4899', '#8b5cf6', '#64748b'];

// Cores semânticas por status do compromisso (mesma paleta usada em Compromisso).
const CORES_STATUS: Record<string, string> = {
  'Incluído': '#3b82f6',
  'Pagando': '#f97316',
  'Pago': '#22c55e',
  'Cancelado': '#eab308',
};

// Cores do aging: do saudável (a vencer) ao crítico (60+ dias).
const CORES_AGING = ['#22c55e', '#eab308', '#f97316', '#ef4444'];

// Constrói as opções de um gráfico de colunas com valores monetários (BRL).
const montarOpcoesBarraMoeda = (titulo: string, itens: ValorItem[], cor: string, cores?: string[]): any => ({
  chart: { type: 'column', height: 320 },
  title: { text: titulo },
  credits: { enabled: false },
  xAxis: { categories: (itens || []).map((i) => i.label) },
  yAxis: {
    title: { text: 'Valor' },
    labels: { formatter: function (this: any) { return formatarParaMoedaBRL(this.value); } },
  },
  legend: { enabled: false },
  tooltip: {
    formatter: function (this: any) {
      return '<b>' + this.x + '</b><br/>' + formatarParaMoedaBRL(this.y);
    },
  },
  plotOptions: {
    column: {
      borderRadius: 3,
      color: cor,
      colorByPoint: !!cores,
      colors: cores,
      dataLabels: {
        enabled: true,
        formatter: function (this: any) { return formatarParaMoedaBRL(this.y); },
        style: { fontSize: '0.7rem' },
      },
    },
  },
  series: [{ name: 'Valor', data: (itens || []).map((i) => Number(i.valor)) }],
});

// Constrói as opções de um gráfico de série temporal de consultas. Com mais de
// uma série (visão do gestor) empilha por representante e mostra legenda; com
// série única mostra rótulos de dados. Usa colunas para poucos pontos (mês) ou
// área para muitos (dia).
const montarOpcoesSerieQuantidade = (titulo: string, dados: SerieTemporal | null, tipo: 'column' | 'areaspline'): any => {
  const categorias = dados?.categorias || [];
  const series = (dados?.series || []).map((s) => ({ name: s.nome, data: (s.dados || []).map(Number) }));
  const multi = series.length > 1;
  return {
    chart: { type: tipo, height: 320 },
    title: { text: titulo },
    credits: { enabled: false },
    colors: CORES_SERIE,
    xAxis: { categories: categorias },
    yAxis: {
      title: { text: 'Consultas' },
      allowDecimals: false,
      stackLabels: { enabled: multi },
    },
    legend: { enabled: series.length > 0 },
    tooltip: multi
      ? {
        shared: true,
        formatter: function (this: any) {
          let texto = '<b>' + this.x + '</b>';
          let total = 0;
          (this.points || []).forEach((p: any) => { texto += '<br/>' + p.series.name + ': ' + p.y; total += p.y; });
          return texto + '<br/><b>Total: ' + total + '</b>';
        },
      }
      : {
        formatter: function (this: any) {
          return '<b>' + this.x + '</b><br/>' + this.y + ' consulta(s)';
        },
      },
    plotOptions: {
      series: {
        stacking: multi ? 'normal' : undefined,
        marker: { enabled: tipo === 'column' },
      },
      column: {
        borderRadius: 3,
        dataLabels: {
          enabled: !multi,
          style: { fontSize: '0.7rem' },
        },
      },
      areaspline: {
        fillOpacity: multi ? 0.5 : 0.2,
      },
    },
    series,
  };
};

// Constrói as opções de um gráfico de pizza/rosca.
const montarOpcoesPizza = (titulo: string, pontos: any[], formatar: (v: number) => string, donut: boolean): any => ({
  chart: { type: 'pie', height: 320 },
  title: { text: titulo },
  credits: { enabled: false },
  tooltip: {
    formatter: function (this: any) {
      return '<b>' + this.point.name + '</b>: ' + formatar(this.y) + ' (' + Math.round(this.percentage) + '%)';
    },
  },
  plotOptions: {
    pie: {
      innerSize: donut ? '55%' : '0%',
      dataLabels: {
        enabled: true,
        formatter: function (this: any) { return this.point.name + ': ' + formatar(this.y); },
        style: { fontSize: '0.7rem' },
      },
    },
  },
  series: [{ name: titulo, data: pontos }],
});

function KpiCard({ titulo, valor, icone, cor }: { titulo: string; valor: string; icone: string; cor: string }) {
  return (
    <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', height: '100%', marginBottom: 0 }}>
      <span style={{ background: cor, color: '#fff', width: '3rem', height: '3rem', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0 }}>
        <i className={icone}></i>
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{titulo}</div>
        <div style={{ fontSize: '1.15rem', fontWeight: 700 }}>{valor}</div>
      </div>
    </div>
  );
}

export default function DashboardRepresentante() {
  const { t } = useTranslation();
  const [dados, setDados] = useState<any>(null);

  useEffect(() => {
    graficoDashboardRepresentante().then((response) => {
      setDados(response);
    });
  }, []);

  const getMes = () => {
    return ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"][new Date().getMonth()];
  };

  const opcoesReceita = useMemo(() => dados
    ? montarOpcoesBarraMoeda('Receita da carteira (últimos 6 meses)', dados.receitaCarteiraUltimos6Meses, '#0ea5e9')
    : null, [dados]);

  const opcoesCompromisso = useMemo(() => dados
    ? montarOpcoesBarraMoeda('Valores de comissões (últimos 6 meses)', dados.valoresCompromissoUltimos6Meses, '#4c63b6')
    : null, [dados]);

  const opcoesStatus = useMemo(() => dados
    ? montarOpcoesPizza('Comissões do mês por status', (dados.comissoesPorStatusMes || [])
      .filter((i: ValorItem) => Number(i.valor) > 0)
      .map((i: ValorItem) => ({ name: i.label, y: Number(i.valor), color: CORES_STATUS[i.label] })), formatarParaMoedaBRL, true)
    : null, [dados]);

  const opcoesAging = useMemo(() => dados
    ? montarOpcoesBarraMoeda('Comissão futura por vencimento', dados.agingComissaoFutura, '#0b655b', CORES_AGING)
    : null, [dados]);

  const opcoesComissaoFutura = useMemo(() => dados
    ? montarOpcoesBarraMoeda('Comissão futura por representante (faturas em aberto)', dados.comissaoFuturaPorRepresentante, '#0b655b')
    : null, [dados]);

  const opcoesClientes = useMemo(() => dados
    ? montarOpcoesPizza('Quantidade de clientes por representante', (dados.quantidadeClientesPorRepresentante || [])
      .map((i: QuantidadeItem) => ({ name: i.label, y: Number(i.quantidade) })), (v: number) => String(v), false)
    : null, [dados]);

  const opcoesConsultasProprioDia = useMemo(() => {
    if (!dados) return null;
    const serie = dados.consultaProprioRepresentantePorDia;
    const multi = (serie?.series?.length || 0) > 1;
    const titulo = (multi ? 'Consultas de clientes por representante' : 'Consultas do próprio representante') + ' — últimos 30 dias';
    return montarOpcoesSerieQuantidade(titulo, serie, 'areaspline');
  }, [dados]);

  const opcoesConsultasProprioMes = useMemo(() => {
    if (!dados) return null;
    const serie = dados.consultaProprioRepresentantePorMes;
    const multi = (serie?.series?.length || 0) > 1;
    const titulo = (multi ? 'Consultas de clientes por representante' : 'Consultas do próprio representante') + ' — últimos 6 meses';
    return montarOpcoesSerieQuantidade(titulo, serie, 'column');
  }, [dados]);

  const paraPodium = (itens: QuantidadeItem[]): ValorItem[] =>
    (itens || []).map((i) => ({ label: i.label, valor: Number(i.quantidade) }));

  if (!dados) {
    return null;
  }

  const resumo = dados.resumo || {};

  return (
    <Panel className="panel-renda" header={t('gestaoRepresentante')} style={{ width: '99%' }}>
      {/* KPIs */}
      <div className="grid">
        <div className="col-12 md:col-6 lg:col p-fluid">
          <KpiCard titulo="Comissão a receber (mês)" valor={formatarParaMoedaBRL(Number(resumo.comissaoAReceberMes || 0))} icone="pi pi-clock" cor="#3b82f6" />
        </div>
        <div className="col-12 md:col-6 lg:col p-fluid">
          <KpiCard titulo="Comissão paga (mês)" valor={formatarParaMoedaBRL(Number(resumo.comissaoPagaMes || 0))} icone="pi pi-check-circle" cor="#22c55e" />
        </div>
        <div className="col-12 md:col-6 lg:col p-fluid">
          <KpiCard titulo="Comissão futura" valor={formatarParaMoedaBRL(Number(resumo.comissaoFuturaTotal || 0))} icone="pi pi-wallet" cor="#0b655b" />
        </div>
        <div className="col-12 md:col-6 lg:col p-fluid">
          <KpiCard titulo="Clientes na carteira" valor={String(resumo.clientesCarteira || 0)} icone="pi pi-users" cor="#4c63b6" />
        </div>
        <div className="col-12 md:col-6 lg:col p-fluid">
          <KpiCard titulo="Ticket médio / cliente" valor={formatarParaMoedaBRL(Number(resumo.ticketMedioComissaoCliente || 0))} icone="pi pi-dollar" cor="#f59c27" />
        </div>
      </div>

      {/* Receita x Comissões (6 meses) */}
      <div className="grid">
        <div className="col-12 md:col-6 p-fluid">
          <div className="card">{opcoesReceita && <Chart highcharts={Highcharts} options={opcoesReceita} />}</div>
        </div>
        <div className="col-12 md:col-6 p-fluid">
          <div className="card">{opcoesCompromisso && <Chart highcharts={Highcharts} options={opcoesCompromisso} />}</div>
        </div>
      </div>

      {/* Status x Aging */}
      <div className="grid">
        <div className="col-12 md:col-6 p-fluid">
          <div className="card">{opcoesStatus && <Chart highcharts={Highcharts} options={opcoesStatus} />}</div>
        </div>
        <div className="col-12 md:col-6 p-fluid">
          <div className="card">{opcoesAging && <Chart highcharts={Highcharts} options={opcoesAging} />}</div>
        </div>
      </div>

      {/* Pódios de comissão */}
      <div className="grid">
        <div className="col-12 md:col-6 p-fluid">
          <PodiumPanel titulo={'Clientes que mais geram comissão — ' + getMes()} itens={dados.podiumClientesComissaoMes} formatar={formatarParaMoedaBRL} />
        </div>
        <div className="col-12 md:col-6 p-fluid">
          <PodiumPanel titulo={'Representantes que mais ganharam — ' + getMes()} itens={dados.podiumRepresentantesComissaoMes} formatar={formatarParaMoedaBRL} />
        </div>
      </div>

      {/* Consultas por representante */}
      <div className="grid">
        <div className="col-12 md:col-6 p-fluid">
          <PodiumPanel titulo={'Consultas por representante — Hoje'} itens={paraPodium(dados.quantidadeConsultaRepresentanteDia)} formatar={(v) => String(v)} />
        </div>
        <div className="col-12 md:col-6 p-fluid">
          <PodiumPanel titulo={'Consultas por representante — ' + getMes()} itens={paraPodium(dados.quantidadeConsultaRepresentanteMes)} formatar={(v) => String(v)} />
        </div>
      </div>

      {/* Consultas dos usuários do próprio representante (dia x mês) */}
      <div className="grid">
        <div className="col-12 md:col-6 p-fluid">
          <div className="card">{opcoesConsultasProprioDia && <Chart highcharts={Highcharts} options={opcoesConsultasProprioDia} />}</div>
        </div>
        <div className="col-12 md:col-6 p-fluid">
          <div className="card">{opcoesConsultasProprioMes && <Chart highcharts={Highcharts} options={opcoesConsultasProprioMes} />}</div>
        </div>
      </div>

      {/* Comissão futura x Distribuição de clientes */}
      <div className="grid">
        <div className="col-12 md:col-6 p-fluid">
          <div className="card">{opcoesComissaoFutura && <Chart highcharts={Highcharts} options={opcoesComissaoFutura} />}</div>
        </div>
        <div className="col-12 md:col-6 p-fluid">
          <div className="card">{opcoesClientes && <Chart highcharts={Highcharts} options={opcoesClientes} />}</div>
        </div>
      </div>
    </Panel>
  );
}
